/**
 * Page lifecycle telemetry: did the PREVIOUS page end cleanly, and how much
 * JS heap does this one hold?
 *
 * ## Why this exists (hasselbacken/302, 2026-09-29)
 *
 * A Samsung QB24C restarted itself twice, at 21:07Z and 21:36Z, unattended,
 * while playing a protected live channel. The old page had been logging about
 * ten lines a second and then simply stopped, with no `pagehide` flush and no
 * exit reason: Tizen has nothing like Android's `ApplicationExitInfo`. We could
 * not tell a crash from a scheduled restart, and we had no memory figure to
 * test the leading theory (a reload loop leaking) against.
 *
 * ## How it works
 *
 * `localStorage` holds one small record, `{ seq, startedAt, lastBeatAt,
 * clean }`. It is rewritten once a minute (`lastBeatAt`) and marked
 * `clean: true` on `pagehide`. On the next boot:
 *
 * - no record: `unknown` (first run, or the firmware wiped the storage);
 * - `clean: true`: the previous page ended normally (a reload, a self-heal);
 * - `clean: false`: it did NOT: a crash, a kill or a power loss. The boot line
 *   says when it last beat and how long it ran.
 *
 * "Unknown" is never reported as "clean": some firmware wipes WebView storage
 * on a cold boot (see the pairing-persistence note), and an absent record
 * proves nothing.
 *
 * Everything leaves the panel as `[lifecycle]` console lines, which
 * `logShipper.ts` already carries into `deviceLogEntries` and the CMS Device
 * log. No new endpoint, field or collection: nothing on the server changes.
 *
 * The boot lines are written before the shipper starts (it needs a paired
 * screen), so they are also kept here and handed to it (`bootLifecycleLines`).
 */
import type { LogLine } from "./logBuffer";

const KEY = "foyer.lifecycle.v1";
const BEAT_MS = 60_000;
const HEAP_FIRST_MS = 30_000;
const HEAP_EVERY_MS = 5 * 60_000;

interface LifecycleRecord {
  seq: number;
  startedAt: number;
  lastBeatAt: number;
  clean: boolean;
}

export type PreviousExit = "clean" | "unclean" | "unknown";

let installed = false;
let bootLines: LogLine[] = [];
let record: LifecycleRecord | null = null;
let timers: Array<ReturnType<typeof setInterval>> = [];
let removePageHide: (() => void) | null = null;

function read(): LifecycleRecord | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as Partial<LifecycleRecord>;
    if (
      typeof r.seq !== "number" ||
      typeof r.startedAt !== "number" ||
      typeof r.lastBeatAt !== "number"
    ) {
      return null;
    }
    return {
      seq: r.seq,
      startedAt: r.startedAt,
      lastBeatAt: r.lastBeatAt,
      clean: r.clean === true,
    };
  } catch {
    return null;
  }
}

function write(r: LifecycleRecord): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(r));
  } catch {
    /* storage refused: the next boot will say "unknown", never "clean" */
  }
}

function span(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}m` : `${m}m${s % 60}s`;
}

function emit(level: "log" | "warn", message: string): void {
  bootLines.push({ ts: new Date().toISOString(), level, message });
  console[level](message);
}

function sampleHeap(): void {
  const mem = (
    performance as unknown as {
      memory?: {
        usedJSHeapSize?: number;
        totalJSHeapSize?: number;
        jsHeapSizeLimit?: number;
      };
    }
  ).memory;
  const mb = (n: number | undefined) =>
    typeof n === "number" ? `${Math.round(n / 1048576)}MB` : "?";
  if (!mem) {
    if (!heapUnavailableLogged) {
      heapUnavailableLogged = true;
      console.log(
        "[lifecycle] heap: performance.memory is not available on this browser"
      );
    }
    return;
  }
  console.log(
    `[lifecycle] heap used=${mb(mem.usedJSHeapSize)} total=${mb(mem.totalJSHeapSize)} limit=${mb(mem.jsHeapSizeLimit)}`
  );
}
let heapUnavailableLogged = false;

/** Lines emitted at boot, for the log shipper to send once it starts. */
export function bootLifecycleLines(): LogLine[] {
  return [...bootLines];
}

/**
 * Start the lifecycle record, report how the previous page ended, and sample
 * the JS heap. Idempotent. Returns the classification for callers and tests.
 */
export function installPageLifecycle(
  now: () => number = () => Date.now()
): PreviousExit {
  if (installed || typeof window === "undefined") return "unknown";
  installed = true;

  const prev = read();
  const exit: PreviousExit = !prev
    ? "unknown"
    : prev.clean
      ? "clean"
      : "unclean";
  const seq = (prev?.seq ?? 0) + 1;

  if (exit === "unclean" && prev) {
    emit(
      "warn",
      `[lifecycle] previous page ended UNCLEANLY (crash, kill or power loss): ran ${span(prev.lastBeatAt - prev.startedAt)}, last beat ${new Date(prev.lastBeatAt).toISOString()}. Page start #${seq}`
    );
  } else if (exit === "clean" && prev) {
    emit(
      "log",
      `[lifecycle] page start #${seq}: previous page ended cleanly after ${span(prev.lastBeatAt - prev.startedAt)}`
    );
  } else {
    emit(
      "log",
      `[lifecycle] page start #${seq}: no record of the previous page (first run, or the storage was cleared)`
    );
  }

  const t = now();
  record = { seq, startedAt: t, lastBeatAt: t, clean: false };
  write(record);

  timers.push(
    setInterval(() => {
      if (!record) return;
      record = { ...record, lastBeatAt: now() };
      write(record);
    }, BEAT_MS)
  );
  timers.push(setTimeout(sampleHeap, HEAP_FIRST_MS) as never);
  timers.push(setInterval(sampleHeap, HEAP_EVERY_MS));

  const onHide = () => {
    if (!record) return;
    record = { ...record, lastBeatAt: now(), clean: true };
    write(record);
  };
  window.addEventListener("pagehide", onHide);
  removePageHide = () => window.removeEventListener("pagehide", onHide);

  return exit;
}

/** Test seam: forget everything so a test can boot a "new page". */
export function _resetPageLifecycleForTests(): void {
  for (const t of timers) {
    clearInterval(t);
    clearTimeout(t as never);
  }
  timers = [];
  removePageHide?.();
  removePageHide = null;
  installed = false;
  bootLines = [];
  record = null;
  heapUnavailableLogged = false;
}
